/**
 * g1xl-v2#1053 Part 3 — the board must not take lines it does not draw.
 *
 * Since 2026-10-01 Polymarket lists every alternate line of a game as its own
 * market (54 full-game spreads at half-point steps for one CFB game, 21
 * totals); he2's CFB slate is ~15,000 rows, and a dashboard that asks for
 * `sports:[CFB]` pulls every one of them at 250 ms to draw a moneyline pair
 * and — on the Spread/Total reading — the three lines around the Kalshi
 * strike nearest even money per game. This module picks THOSE rows out of
 * a light index of the slate so the board can name them on the `tickers`
 * axis (path 2 of the task: the engines have no line-scope axis yet).
 *
 * The pick mirrors what the board actually renders:
 *   - moneyline: every full-game moneyline row of the game (Kalshi pair +
 *     Poly pair) — the moneyline board draws nothing else, and the
 *     Spread/Total board's row header, status and time read the same rows.
 *   - spread-total: additionally, per venue ladder, the Kalshi strikes in
 *     the nearest-three window around the strike nearest 50¢
 *     (`nearestLineWindow`, pinned by Part 1 / `venueAwareLineWindow`), and
 *     the Poly rows at exactly those lines (spreadMatch.ts: same points,
 *     same covering side). A game Kalshi does not list falls back to the
 *     Poly ladder's own window, so a Poly-only game still draws.
 *
 * Line magnitude follows the store's own precedence (`getGameEvents`):
 * `floorStrike` (structured, hydraedge 2026-07-31) → subtitle → title →
 * `bucket − 0.5` / `strike − 0.5`; Poly: `floorStrike` → the slug's `pt`
 * suffix. Price: mid of a two-sided BBO, else last trade — the same rule as
 * a VenueBook's `priceCents`. Rows the ticker parser cannot place are
 * skipped: the board cannot draw them either.
 *
 * Pure. The index rows are wire-shaped `HydraMarket`s (REST ≡ WS frame).
 */
import type { HydraMarket } from '@/clients/HydraClient';
import { parseTicker, resolveMarketPeriod } from '@/stores/marketFrameNormalization';
import { formatStartDateLocal, parseLineFromSubtitle, parseStartTimeMs } from '@/stores/marketStore';
import { midCents } from '@/lib/pricing/subCent';
import { nearestLineWindow } from '@/views/SpreadsTotals/synthesize';
import { addDaysIso } from '@/views/ValueDashboard/weeklySlate';
import { usLineIdentity } from '@/stores/usBoardQuotes';
import { normalizeUsGameKey } from '@/lib/polymarketUs/sports';
import { usGameSportForBoard } from '@/lib/polymarketUs/sports';
import { usFullGameTotalFamily } from '@/lib/polymarketUs/totalTarget';

export type BoardReading = 'moneyline' | 'spread-total';

export interface BoardPickOptions {
  reading: BoardReading;
  /** The board's inclusive slate window (ISO days, local). Games whose start
   *  falls outside it ± one day of slack are not picked; a game with no
   *  start time is kept (fail-open — the board decides membership). Absent
   *  = every game in the index. */
  dayWindow?: { startIso: string; endIso: string } | null;
}

/** #1231 — the lines the board draws for one game: the drawn spread
 *  magnitudes (unsigned) and total lines, each ascending. */
export interface LineWindow { spreadMagnitudes: number[]; totalLines: number[] }

export interface BoardPick {
  /** The rows the board draws, sorted. */
  tickers: string[];
  /** #1231 — per game key (V2 preferred), the line window the pick drew.
   *  Empty on the moneyline reading. */
  windows: ReadonlyMap<string, LineWindow>;
  /** Games that contributed at least one row. */
  games: number;
  /** The largest per-game row count in this pick — the figure the ceiling
   *  test reads. */
  maxRowsPerGame: number;
}

/** Documented per-game ceiling of what the board subscribes: the moneyline
 *  pair per venue (≤ 4) plus, on the Spread/Total reading, a three-line
 *  window per venue for spreads and for totals (≤ 12). */
export const BOARD_ROWS_PER_GAME_CEILING: Record<BoardReading, number> = {
  moneyline: 4,
  'spread-total': 16,
};

interface LadderRow {
  ticker: string;
  /** Signed ordering value: spreads by covering side (home negative, away
   *  positive — any consistent convention works, both venues use the same),
   *  totals by points. */
  pos: number;
  /** Venue-independent line identity: `${side}:${mag}` for spreads, `${mag}`
   *  for totals. */
  key: string;
  price: number;
}

interface GameAccum {
  moneyline: string[];
  kalshiSpread: LadderRow[];
  polySpread: LadderRow[];
  kalshiTotal: LadderRow[];
  polyTotal: LadderRow[];
  /** Local start dates seen on the game's rows (Kalshi first when present). */
  days: Set<string>;
}

function rowPriceCents(m: HydraMarket): number | null {
  const bid = m.yesBid;
  const ask = m.yesAsk;
  if (bid != null && bid > 0 && ask != null && ask > 0 && ask < 100) return midCents(bid, ask);
  const last = m.lastPrice;
  return last != null && last > 0 ? last : null;
}

/** The nearest-three window around the row nearest even money, in ladder
 *  order — empty when nothing is priced. */
function drawnWindow(rows: LadderRow[]): LadderRow[] {
  if (rows.length === 0) return [];
  const sorted = [...rows].sort((a, b) => a.pos - b.pos || a.ticker.localeCompare(b.ticker));
  let anchor = 0;
  let best = Infinity;
  sorted.forEach((r, i) => {
    const d = Math.abs(r.price - 50);
    if (d < best) { best = d; anchor = i; }
  });
  return nearestLineWindow(sorted.length, anchor).map((i) => sorted[i]);
}

/** Kalshi window first; Poly rows at the same lines; Poly's own window when
 *  Kalshi lists nothing for this ladder. */
function pickLadder(kalshi: LadderRow[], poly: LadderRow[]): string[] {
  const k = drawnWindow(kalshi);
  if (k.length === 0) return drawnWindow(poly).map((r) => r.ticker);
  const keys = new Set(k.map((r) => r.key));
  return [...k.map((r) => r.ticker), ...poly.filter((r) => keys.has(r.key)).map((r) => r.ticker)];
}

function inWindow(days: Set<string>, win: { startIso: string; endIso: string } | null | undefined): boolean {
  if (!win || days.size === 0) return true;
  const lo = addDaysIso(win.startIso, -1);
  const hi = addDaysIso(win.endIso, 1);
  for (const d of days) if (d >= lo && d <= hi) return true;
  return false;
}

/** The window the board actually DRAWS. The dashboard's rule (#757,
 *  `pickUpcomingSlate`): when the requested window has no games it falls
 *  forward to the first day at or after it that has some, widened to the
 *  window's own length — a Thursday NFL board shows Sunday's slate. The
 *  pick must land on the same games or the board draws rows the feed was
 *  never asked for (dan.node1, 2026-10-02: an NFL board on a Thursday
 *  declared an empty allow-list, he5 sent nothing, and Polymarket — which
 *  only he5 serves — vanished from the board while hc1's floor kept the
 *  Kalshi rows on screen). Null = every game. */
export function effectiveDayWindow(
  gameDays: Iterable<ReadonlySet<string>>,
  win: { startIso: string; endIso: string } | null | undefined,
): { startIso: string; endIso: string } | null {
  if (!win) return null;
  const days = new Set<string>();
  for (const d of gameDays) for (const day of d) days.add(day);
  if (days.size === 0) return win;
  const lo = addDaysIso(win.startIso, -1);
  const hi = addDaysIso(win.endIso, 1);
  for (const d of days) if (d >= lo && d <= hi) return win;
  const ahead = [...days].filter((d) => d >= win.startIso).sort();
  if (ahead.length === 0) return win;
  const spanDays = Math.max(0, Math.round((Date.UTC(
    Number(win.endIso.slice(0, 4)), Number(win.endIso.slice(5, 7)) - 1, Number(win.endIso.slice(8, 10)),
  ) - Date.UTC(
    Number(win.startIso.slice(0, 4)), Number(win.startIso.slice(5, 7)) - 1, Number(win.startIso.slice(8, 10)),
  )) / 86_400_000));
  return { startIso: ahead[0], endIso: addDaysIso(ahead[0], spanDays) };
}

export function pickBoardTickers(rows: readonly HydraMarket[], opts: BoardPickOptions): BoardPick {
  const games = new Map<string, GameAccum>();
  const accum = (key: string): GameAccum => {
    let g = games.get(key);
    if (!g) {
      g = { moneyline: [], kalshiSpread: [], polySpread: [], kalshiTotal: [], polyTotal: [], days: new Set() };
      games.set(key, g);
    }
    return g;
  };
  for (const m of rows) {
    const key = m.canonicalGameKeyV2 ?? m.canonicalGameKey ?? null;
    if (!key) continue;
    const parsed = parseTicker(m);
    if (!parsed) continue;
    if (resolveMarketPeriod(m, parsed) !== 'full') continue;
    const type = parsed.marketType;
    if (type !== 'moneyline' && type !== 'spread' && type !== 'total') continue;
    const g = accum(key);
    const day = formatStartDateLocal(parseStartTimeMs(m.eventStartTime));
    if (day) g.days.add(day);
    if (type === 'moneyline') { g.moneyline.push(m.ticker); continue; }
    if (opts.reading !== 'spread-total') continue;
    const price = rowPriceCents(m);
    if (price == null) continue;
    if (m.source === 'kalshi') {
      if (type === 'spread') {
        const mag = m.floorStrike ?? parseLineFromSubtitle(m.subtitle) ?? parseLineFromSubtitle(m.title)
          ?? (parsed.spreadBucket != null ? parsed.spreadBucket - 0.5 : null);
        // Moneyline-equivalent buckets (mag < 1) live in the game market —
        // the store drops them from the spread ladder, so does the pick.
        if (mag == null || mag < 1) continue;
        const side = parsed.side;
        g.kalshiSpread.push({ ticker: m.ticker, pos: side === 'home' ? -mag : mag, key: `${side}:${mag}`, price });
      } else {
        const mag = m.floorStrike ?? parseLineFromSubtitle(m.subtitle)
          ?? (parsed.totalStrike != null ? parsed.totalStrike - 0.5 : null);
        if (mag == null) continue;
        g.kalshiTotal.push({ ticker: m.ticker, pos: mag, key: `${mag}`, price });
      }
    } else {
      const mag = m.floorStrike ?? parsed.polyLine ?? null;
      if (mag == null) continue;
      if (type === 'spread') {
        const side = parsed.polySpreadSide ?? parsed.side;
        g.polySpread.push({ ticker: m.ticker, pos: side === 'home' ? -mag : mag, key: `${side}:${mag}`, price });
      } else {
        g.polyTotal.push({ ticker: m.ticker, pos: mag, key: `${mag}`, price });
      }
    }
  }
  const tickers = new Set<string>();
  const windows = new Map<string, LineWindow>();
  let picked = 0;
  let maxRows = 0;
  const window = effectiveDayWindow([...games.values()].map((g) => g.days), opts.dayWindow);
  for (const [key, g] of games) {
    if (!inWindow(g.days, window)) continue;
    const rowsForGame = [...g.moneyline];
    if (opts.reading === 'spread-total') {
      rowsForGame.push(...pickLadder(g.kalshiSpread, g.polySpread));
      rowsForGame.push(...pickLadder(g.kalshiTotal, g.polyTotal));
      const spread = anchorWindow(g.kalshiSpread, g.polySpread), total = anchorWindow(g.kalshiTotal, g.polyTotal);
      if (spread.length > 0 || total.length > 0) {
        windows.set(key, { spreadMagnitudes: ascending(spread.map((r) => Math.abs(r.pos))), totalLines: ascending(total.map((r) => r.pos)) });
      }
    }
    if (rowsForGame.length === 0) continue;
    picked++;
    const distinct = new Set(rowsForGame);
    if (distinct.size > maxRows) maxRows = distinct.size;
    for (const t of distinct) tickers.add(t);
  }
  return { tickers: [...tickers].sort(), games: picked, maxRowsPerGame: maxRows, windows };
}

/** The window `pickLadder` anchors on: Kalshi's, or Poly's own when Kalshi lists nothing. */
function anchorWindow(kalshi: LadderRow[], poly: LadderRow[]): LadderRow[] {
  const k = drawnWindow(kalshi);
  return k.length > 0 ? k : drawnWindow(poly);
}
const ascending = (xs: number[]): number[] => [...new Set(xs)].sort((a, b) => a - b);

/** #1231 — the documented ceiling of US line names per game: a drawn spread
 *  magnitude may be listed for either YES team (3 × 2) plus 3 totals. A worked
 *  game is the exception, as on K/P: every full-game line of that one game. */
export const US_LINE_NAMES_PER_GAME_CEILING = 9;

const sameNumber = (a: number, b: number): boolean => Math.round(a * 100) === Math.round(b * 100);

/** #1262: preserve existing K/Intl windows; where a native US-only ladder has
 * none, seed at most three exact lines. Index BBO chooses demand only, never
 * a displayed quote. The live displayed-line declaration follows selection. */
export function withUsLineWindows(rows: readonly HydraMarket[], existing: ReadonlyMap<string, LineWindow>): ReadonlyMap<string, LineWindow> {
  const windows = new Map(existing), groups = new Map<string, { spreads: LadderRow[]; totals: LadderRow[] }>();
  for (const row of rows) {
    const line = usLineIdentity(row);
    if (!line) continue;
    const key = line.game.wireKey;
    let group = groups.get(key);
    if (!group) { group = { spreads: [], totals: [] }; groups.set(key, group); }
    const ladder = line.marketType === 'spreads' ? group.spreads : group.totals;
    const pos = line.marketType === 'spreads' ? Math.abs(line.line) : line.line;
    // Either native orientation of a spread has the same distance from 50.
    // Stable fallback when no index BBO exists; no last-price invention.
    const price = typeof row.yesBid === 'number' && typeof row.yesAsk === 'number'
      && row.yesBid >= 0 && row.yesAsk <= 100 && row.yesBid <= row.yesAsk ? (row.yesBid + row.yesAsk) / 2 : NaN;
    const prior = ladder.find(r => r.pos === pos);
    if (!prior) ladder.push({ ticker: row.ticker, pos, key: String(pos), price });
    else if (!Number.isFinite(prior.price) && Number.isFinite(price)) prior.price = price;
    if (!windows.has(key)) {
      const match = [...existing].find(([k]) => normalizeUsGameKey(k, line.game.sport) === line.game.key)?.[1];
      if (match) windows.set(key, match);
    }
  }
  for (const [key, group] of groups) {
    const previous = windows.get(key);
    windows.set(key, {
      spreadMagnitudes: previous?.spreadMagnitudes.length ? previous.spreadMagnitudes : ascending(drawnWindow(group.spreads).map(r => r.pos)),
      totalLines: previous?.totalLines.length ? previous.totalLines : ascending(drawnWindow(group.totals).map(r => r.pos)),
    });
  }
  return windows;
}

/** #1231 — the US full-game spread and total tickers for the drawn line
 *  windows: a spread when its |strike| is one of its game's drawn magnitudes,
 *  a total when its strike is one of the drawn total lines; every full-game
 *  line of a worked game. Magnitude only: the canonical key is alphabetical,
 *  so no side is inferred here (`pickUsSpread` does that, exactly, per row).
 *  Never a period line, team total, prop, closed market or another game. */
export function pickUsLineTickers(rows: readonly HydraMarket[], windows: ReadonlyMap<string, LineWindow>,
  workedGames: ReadonlySet<string> = new Set()): string[] {
  const names = new Set<string>();
  for (const m of rows) {
    const r = m as unknown as Record<string, unknown>;
    const key = typeof r.canonicalGameKeyV2 === 'string' ? r.canonicalGameKeyV2 : null;
    const slug = r.pmusMarketSlug, type = r.sportsMarketType, strike = r.floorStrike;
    if (!key || r.source !== 'polymarket_us' || r.venue !== 'polymarket_us' || r.marketType !== 'game'
      || r.status !== 'open' || r.period !== 'full' || typeof slug !== 'string' || !/^[A-Za-z0-9_-]{1,256}$/.test(slug)
      || m.ticker !== `PMUS-${slug}` || typeof type !== 'string' || typeof strike !== 'number' || !Number.isFinite(strike)) continue;
    const window = windows.get(key), worked = workedGames.has(key);
    if (!window && !worked) continue;
    const base = typeof r.baseSport === 'string' ? r.baseSport : r.sport;
    const sport = typeof base === 'string' ? usGameSportForBoard(base) : null;
    if (r.pmusMarketType === 'spreads') {
      const team = r.canonicalYesTeam;
      if (!/full_game_spread$/.test(type) || typeof team !== 'string' || !key.split('|').slice(3).includes(team)) continue;
      if (worked || window!.spreadMagnitudes.some((mag) => sameNumber(mag, Math.abs(strike)))) names.add(m.ticker);
    } else if (r.pmusMarketType === 'totals') {
      if (!sport || !usFullGameTotalFamily(type, sport) || r.canonicalYesTeam != null || !(strike > 0)) continue;
      if (worked || window!.totalLines.some((line) => sameNumber(line, strike))) names.add(m.ticker);
    }
  }
  return [...names].sort();
}

/** The game keys (V2 preferred) of the index rows inside the board's day
 *  window — the second step of the two-step index: the moneyline index names
 *  the games, the ladder index is fetched for those games only. */
export function boardGameKeys(rows: readonly { canonicalGameKeyV2?: string | null; canonicalGameKey?: string | null; eventStartTime?: string | null }[], dayWindow?: { startIso: string; endIso: string } | null): string[] {
  const days = new Map<string, Set<string>>();
  for (const m of rows) {
    const key = m.canonicalGameKeyV2 ?? m.canonicalGameKey ?? null;
    if (!key) continue;
    let d = days.get(key);
    if (!d) { d = new Set(); days.set(key, d); }
    const day = formatStartDateLocal(parseStartTimeMs(m.eventStartTime));
    if (day) d.add(day);
  }
  const window = effectiveDayWindow(days.values(), dayWindow);
  const out: string[] = [];
  for (const [key, d] of days) if (inWindow(d, window)) out.push(key);
  return out.sort();
}
